import { RefObject, useState } from 'react';

import { BookmarkAddOutlined, KeyboardArrowDown, Launch } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  Grow,
  List,
  ListItem,
  Paper,
  Popper,
  Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { SelectorButton } from '@/components/SymbolSelector/components/SelectorButton';
import useDataHubSelector from '@/pages/DataHub/useDataHubSelector';
import { FACTSHEET_PARAM_NAME } from '@/pages/Facthsheets/constants';
import { getTimeSince } from '@/utils/getTimeSince';
import { RootState } from 'store';
import { DataHub } from 'types/dataHub';

const DataHubItem = ({
  dataHub,
  onItemClick
}: {
  dataHub: DataHub;
  onItemClick?: (dataHubId: string) => void;
}) => {
  const { addSymbolsToExistingDataHub } = useDataHubSelector();

  return (
    <ListItem
      sx={theme => ({
        py: 1,
        pl: 1,
        pr: 0,
        mb: 1,
        height: 46,
        minWidth: 187,
        cursor: 'pointer',
        backgroundColor: `${theme.palette.text.primary}33`,
        '&:hover': {
          backgroundColor: `${theme.palette.text.primary}66`
        }
      })}
      onClick={() => {
        if (onItemClick) {
          onItemClick(dataHub.id);
        }
        addSymbolsToExistingDataHub(dataHub.id);
      }}
    >
      <Box
        alignItems='flex-start'
        display='flex'
        flexDirection='column'
      >
        <Typography
          noWrap
          sx={{ fontWeight: 500 }}
          variant='caption'
        >
          {dataHub.name}
        </Typography>
        <Typography
          sx={{ fontSize: 10 }}
          variant='caption'
        >{`${dataHub.symbols.length} items, last saved ${getTimeSince(dataHub.savedAt)}`}</Typography>
      </Box>
    </ListItem>
  );
};

const DataHubsDropdown = ({
  popperAnchorRef,
  onDataHubItemClick
}: {
  popperAnchorRef: RefObject<HTMLElement | undefined>;
  onDataHubItemClick?: (dataHubId: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { dataHubs } = useSelector((state: RootState) => state.dataHub);

  const togglePopper = () => {
    if (open) {
      setOpen(false);
      setAnchorEl(null);
    } else {
      // Safe to read ref in event handler (outside of render)
      setAnchorEl(popperAnchorRef?.current || null);
      setOpen(true);
    }
  };

  const { createNewDataHub } = useDataHubSelector();

  const [, setSearchParams] = useSearchParams();
  const closeFactsheetWindow = () => {
    setSearchParams(searchParams => {
      searchParams.delete(FACTSHEET_PARAM_NAME);
      return searchParams;
    });
  };

  return (
    <>
      <SelectorButton
        sx={{ width: 187, mr: 0, justifyContent: 'flex-start' }}
        onClick={togglePopper}
      >
        <Launch />
        <Typography
          sx={{ mr: 0.5 }}
          variant='caption'
        >
          Add to Data Hub
        </Typography>
        <KeyboardArrowDown
          sx={{
            ml: 'auto'
          }}
        />
      </SelectorButton>
      <Popper
        disablePortal
        transition
        anchorEl={anchorEl}
        open={open}
        placement='bottom-end'
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                  setAnchorEl(null);
                }}
              >
                <List
                  className='scroll-thin'
                  sx={{
                    width: '100%',
                    backgroundColor: 'primary.main',
                    px: 1,
                    pt: 1,
                    pb: 1,
                    '.MuiListItem-padding:last-of-type': { marginBottom: 0 },
                    maxWidth: 360,
                    maxHeight: 162,
                    overflowY: 'scroll',
                    overflowX: 'hidden'
                  }}
                >
                  <SelectorButton
                    sx={{
                      marginBottom: 1,
                      flex: 1,
                      width: '100%',
                      justifyContent: 'flex-start'
                    }}
                    onClick={() => {
                      closeFactsheetWindow();
                      createNewDataHub();
                    }}
                  >
                    <BookmarkAddOutlined />
                    <Typography variant='caption'>Create new Data Hub</Typography>
                  </SelectorButton>
                  {dataHubs.map(dataHub => (
                    <DataHubItem
                      key={dataHub.name + dataHub.id}
                      dataHub={dataHub}
                      onItemClick={onDataHubItemClick}
                    />
                  ))}
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default DataHubsDropdown;
